/**
 * Hero da ficha: monograma, nome (elemento compartilhado com a linha da
 * lista), chips de pedido/vendedor/grupo, status de risco, ações e os seis
 * indicadores com a barra dos ambientes por status.
 */
import React from "react";
import { motion } from "framer-motion";
import { FiCopy, FiMessageCircle, FiPlus } from "react-icons/fi";

import { useToast } from "../../../../context/ToastContext";
import { formatarReaisBR, plural } from "../../../../utils/dinheiro";
import { AnelDeSaude } from "../comum/Graficos";
import { CURVA_ENTRADA, DURACAO, ENTRADA_DE_CARD, transicaoDeCard } from "../comum/movimento";
import { ROTULO_DA_FAIXA } from "../dados/regras";
import { iniciais } from "../dados/ficha";

const OCULTO = "••••••";

function Indicador({ rotulo, tom, valor, sufixo, legenda, indice, privado }) {
  return (
    <motion.div className={`cl-st${tom ? ` ${tom}` : ""}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DURACAO.componente, ease: CURVA_ENTRADA, delay: 0.1 + indice * 0.04 }}>
      <span className="cl-eyebrow">{rotulo}</span>
      <div className={`cl-st-n${privado ? " priv" : ""}`}>{valor}{sufixo && <small> {sufixo}</small>}</div>
      <div className="cl-st-c">{legenda}</div>
    </motion.div>
  );
}

function BarraDeAmbientes({ fatias, comPendenciaDeTerceiros, total }) {
  const descricao = fatias.map((f) => `${f.total} ${f.rotulo.toLowerCase()}`).join(", ");
  return (
    <div className="cl-mbar">
      <motion.div className="cl-mbar-t" role="img" aria-label={`${plural(total, "ambiente", "ambientes")}: ${descricao}`}
        initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: DURACAO.barras, ease: CURVA_ENTRADA, delay: 0.25 }}>
        {fatias.map((f) => <i key={f.status} style={{ flex: f.total, background: f.cor }} title={`${f.rotulo}: ${f.total}`} />)}
      </motion.div>
      <div className="cl-mbar-l">
        {fatias.map((f) => (
          <span key={f.status}><i style={{ background: f.cor }} />{f.rotulo} <b>{f.total}</b></span>
        ))}
        {comPendenciaDeTerceiros > 0 && (
          <span className="cl-nota">
            {plural(comPendenciaDeTerceiros, "ambiente entregue ainda aguarda", "ambientes entregues ainda aguardam")} terceiros ou acabamento
          </span>
        )}
      </div>
    </div>
  );
}

export default function Hero({
  cliente, indicadores, fatias, precosVisiveis, conversa, aoAbrirConversa, aoNovaObservacao, podeComentar,
}) {
  const toast = useToast();
  const vendedor = cliente.vendedores.find((v) => v && v !== "—");
  const ind = indicadores;
  const statusDeRisco = `${ROTULO_DA_FAIXA[ind.faixa]}${ind.atraso ? ` · ${ind.atraso} d acumulados` : ""}`;

  async function copiarNome() {
    try {
      await navigator.clipboard.writeText(cliente.cliente);
      toast.success("Nome copiado.");
    } catch {
      toast.error("Não foi possível copiar o nome.");
    }
  }

  return (
    <motion.section className="cl-hero" aria-label="Resumo do cliente"
      initial={ENTRADA_DE_CARD.inicial} animate={ENTRADA_DE_CARD.final} transition={transicaoDeCard(0)}>
      <div className="cl-hero-top">
        <div className="cl-idn">
          <span className="cl-mono-av" aria-hidden="true">{iniciais(cliente.cliente)}</span>
          <div style={{ minWidth: 0 }}>
            <div className="cl-idn-n">
              <h1 className="cl-hero-nome">{cliente.cliente}</h1>
              <button type="button" className="cl-ib" aria-label="Copiar nome do cliente" title="Copiar" onClick={copiarNome}>
                <FiCopy size={15} />
              </button>
            </div>
            <div className="cl-tags">
              {cliente.numerosDosPedidos.map((n) => <span key={n} className="cl-tag cl-mono">#{n}</span>)}
              <span className="cl-tag">
                {plural(cliente.totalPedidos, "pedido", "pedidos")} · {plural(cliente.totalAmbientes, "ambiente", "ambientes")}
              </span>
              {vendedor && <span className="cl-tag">Vendedor <b>{vendedor}</b></span>}
              {conversa && (
                <span className="cl-tag wa"><FiMessageCircle size={13} aria-hidden="true" />{conversa.nome}</span>
              )}
            </div>
          </div>
        </div>
        <div className="cl-h-acts">
          <span className={`cl-pill p-${ind.faixa}`}><span>{statusDeRisco}</span></span>
          {conversa && (
            <button type="button" className="cl-btn" onClick={aoAbrirConversa}>
              <FiMessageCircle size={15} aria-hidden="true" />Abrir conversa
            </button>
          )}
          {podeComentar && (
            <button type="button" className="cl-btn pri" onClick={aoNovaObservacao}>
              <FiPlus size={15} aria-hidden="true" />Nova observação
            </button>
          )}
        </div>
      </div>

      <div className="cl-stats">
        <motion.div className="cl-st" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURACAO.componente, ease: CURVA_ENTRADA, delay: 0.1 }}>
          <span className="cl-eyebrow">Saúde</span>
          <div className="cl-st-ring">
            <span className="cl-hero-anel">
              <AnelDeSaude saude={ind.saude} faixa={ind.faixa} atraso={0.2}
                rotulo={`Saúde ${ind.saude}% (${ROTULO_DA_FAIXA[ind.faixa]})`} />
            </span>
            <span className="cl-st-c">{ind.saudeLegenda}</span>
          </div>
        </motion.div>
        <Indicador indice={1} rotulo="Em andamento" tom="acc" valor={ind.emAndamento} legenda={ind.emAndamentoLegenda} />
        <Indicador indice={2} rotulo="Entregues" tom="ok" valor={ind.entregues} sufixo={`/ ${ind.total}`} legenda={ind.entreguesLegenda} />
        <Indicador indice={3} rotulo="Atraso acumulado" tom={ind.atraso ? "bad" : ""} valor={ind.atraso} sufixo="dias"
          legenda={ind.atrasoLegenda} />
        <Indicador indice={4} rotulo="Aguard. liberação" valor={ind.aguardandoLiberacao} legenda={ind.liberacaoLegenda} />
        <Indicador indice={5} rotulo="Valor total" valor={precosVisiveis ? formatarReaisBR(ind.valor) : OCULTO}
          privado={!precosVisiveis} legenda={ind.valorLegenda} />
      </div>

      <BarraDeAmbientes fatias={fatias} comPendenciaDeTerceiros={ind.comPendenciaDeTerceiros} total={ind.total} />
    </motion.section>
  );
}
